<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';

import {
  ApplicantType,
  Gender,
  genderOptions,
  RelationshipType,
  relationshipTypeOptions,
  YesNoOptions,
} from '@/helpers/const';
import dayjs from '@/helpers/dayjs';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';
import { capitalise } from '@/helpers/format';
import { noWhiteSpaceKeys } from '@/helpers/noWhiteSpaceKeys';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';
import { applicantSchema } from '@/validation';
import { applicantSimpleSchema } from '@/validation/schemas/simple/applicant';

import type { Applicant, ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';
import { useRelationshipManager } from '@/helpers/useRelationshipManager';

const props = withDefaults(
  defineProps<{
    type: ApplicantTypeEnum;
    modelValue: ApplicantEntry;
    primary?: ApplicantEntry;
    allApplicants?: ApplicantEntry[];
    currentId?: string;
  }>(),
  {
    primary: undefined,
    allApplicants: () => [],
    currentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ApplicantEntry): void;
  (e: 'relationship-change', applicantId: string, newStatus: string, oldStatus?: string): void;
}>();

const { loanApp, sectionStates } = useCustomerQuoteProcess();

const applicantDetailsType = computed(() => sectionStates.value.applicantDetails);

const applicantsRef = computed(() => props.allApplicants || []);

const { getRelationshipOptions, validate } = useRelationshipManager(applicantsRef);

const relationshipOptions = computed(() => {
  return getRelationshipOptions(props.currentId);
});

const brokerId = loanApp.value.brokerId ?? '';
const overFiftyFiveSelection = ref<'yes' | 'no' | undefined>();

const validationSchema = computed(() => {
  if (applicantDetailsType.value === 'simple') {
    return applicantSimpleSchema();
  } else {
    return applicantSchema(props.primary, props.allApplicants, props.currentId);
  }
});

const { meta, submitCount, values, handleSubmit, resetForm, setFieldValue } = useForm<ApplicantEntry>({
  validationSchema: validationSchema,
  validateOnMount: false,
  initialValues: { ...props.modelValue, type: props.type },
});

const relationshipErrors = computed(() => {
  const allErrors = validate.value;
  const id = props.currentId;
  if (!id) return [];
  return allErrors.filter((error) => error.includes(`[${id}]`));
});

const shouldShowRelationshipErrors = computed(() => {
  return relationshipErrors.value.length > 0 && (submitCount.value > 0 || meta.value.touched);
});

const update = handleSubmit(
  (values) => {
    if (values.relationshipStatus !== props.modelValue.relationshipStatus) {
      emit(
        'relationship-change',
        props.currentId,
        values.relationshipStatus || '',
        props.modelValue.relationshipStatus || '',
      );
    }
    emit('update:modelValue', values);
    forceScrollToTop(0);
    return true;
  },
  () => false,
);

const cancel = () => {
  resetForm();
  forceScrollToTop(0);
};

const updatePreferredName = () => {
  values.firstName = capitalise(values.firstName);
  if (values.firstName && !values.preferredName) {
    setFieldValue('preferredName', values.firstName);
  }
};

const onlyValidPhoneKeys = (event: KeyboardEvent) => {
  if ((event.key < '0' || event.key > '9') && event.key !== '+') {
    event.preventDefault();
  }
};

defineExpose({ update, cancel, resetForm, setFieldValue });

watch(
  () => props.modelValue,
  (applicant) => {
    const values = { ...applicant, type: props.type } as Applicant;
    resetForm({ values });

    if (applicant?.birthdate) {
      const age = dayjs().diff(applicant.birthdate, 'year');
      overFiftyFiveSelection.value = age > 55 ? 'yes' : 'no';
    } else {
      overFiftyFiveSelection.value = undefined;
    }
  },
  { immediate: true, deep: true },
);

watch(overFiftyFiveSelection, (selection) => {
  if (!selection) return;

  if (selection === 'yes') {
    const birthdate = dayjs().subtract(60, 'year').format('YYYY-MM-DD');
    setFieldValue('birthdate', birthdate);
  } else {
    const birthdate = dayjs().subtract(30, 'year').format('YYYY-MM-DD');
    setFieldValue('birthdate', birthdate);
  }
});

watch(
  () => applicantsRef.value,
  (newApplicants) => {
    const currentApplicant = newApplicants.find((a) => a.id === props.currentId);
    if (currentApplicant && currentApplicant.relationshipStatus !== values.relationshipStatus) {
      setFieldValue('relationshipStatus', currentApplicant.relationshipStatus);
    }
  },
  { deep: true },
);
</script>

<template>
  <form>
    <div class="form-group">
      <BxField v-slot="{ field }" name="firstName">
        <BxInput
          :id="values.id + '-first-name'"
          v-bind="field"
          class="form-control"
          label="First name"
          label-type="floating"
          @blur="updatePreferredName()"
        />
      </BxField>
      <BxField v-if="applicantDetailsType === 'comprehensive'" v-slot="{ field }" name="middleName">
        <BxInput
          :id="values.id + '-middle-name'"
          v-bind="field"
          class="form-control"
          label="Middle name"
          label-type="floating"
          @blur="values.middleName = capitalise(values.middleName)"
        />
      </BxField>
      <BxField v-slot="{ field }" name="lastName">
        <BxInput
          :id="values.id + '-last-name'"
          v-bind="field"
          class="form-control"
          label="Last name"
          label-type="floating"
          @blur="values.lastName = capitalise(values.lastName)"
        />
      </BxField>
      <BxField v-if="applicantDetailsType === 'comprehensive'" v-slot="{ field }" name="preferredName">
        <BxInput
          :id="values.id + '-preferred-name'"
          v-bind="field"
          class="form-control"
          label="Preferred name"
          label-type="floating"
        />
      </BxField>
    </div>
    <div v-if="type === ApplicantType.SECONDARY && applicantDetailsType === 'comprehensive'" class="mb-10">
      <BxField v-slot="{ field }" name="relationshipToPrimary">
        <BxRadio
          :id="values.id + '-relationship-to-primary'"
          v-bind="field"
          class="form-control !mt-10"
          :options="relationshipTypeOptions"
          :label="`What is your relationship with ${props.primary?.preferredName}?`"
          label-type="default"
        />
      </BxField>
      <BxField
        v-if="values.relationshipToPrimary === RelationshipType.OTHER"
        v-slot="{ field }"
        name="relationshipOtherInfo"
      >
        <BxInput v-bind="field" label="Other relationship info" label-type="floating" class="mt-3" />
      </BxField>
    </div>
    <BxField v-slot="{ field }" name="relationshipStatus">
      <BxSelect
        :id="values.id + '-relationship-status'"
        class="form-control mt-5"
        v-bind="field"
        :list-items="relationshipOptions"
        label="Relationship status"
        label-type="floating"
      />
      <div v-if="shouldShowRelationshipErrors" class="mt-2 text-sm text-red-600">
        <p
          v-for="error in relationshipErrors.map((error) => error.replace(/^\[[^\]]+\]\s*/, ''))"
          :key="error"
          class="mb-1"
        >
          {{ error }}
        </p>
      </div>
    </BxField>
    <div v-if="applicantDetailsType === 'comprehensive'" class="form-control !mt-10">
      <BxField v-slot="{ field }" name="gender">
        <BxRadio
          :id="values.id + '-gender'"
          v-bind="field"
          :options="genderOptions"
          label="Gender"
          label-type="default"
          block
        />
      </BxField>
      <Transition name="fade">
        <div v-show="values.gender === Gender.OTHER">
          <BxField v-slot="{ field }" name="genderOther">
            <BxInput class="ml-7 mt-3" v-bind="field" label="" placeholder="Describe your gender identity" />
          </BxField>
        </div>
      </Transition>
    </div>
    <BxField v-if="applicantDetailsType === 'simple'" v-slot="{ field }" name="birthdate">
      <BxSelect
        v-bind="{ ...field, onFocusout: undefined }"
        :id="values.id + '-over-fifty-five'"
        v-model="overFiftyFiveSelection"
        class="form-control"
        :list-items="YesNoOptions"
        label="Over 55?"
        label-type="floating"
      />
    </BxField>
    <BxField v-else v-slot="{ field }" name="birthdate">
      <BxDateInput
        :id="values.id + '-date-of-birth'"
        v-bind="field"
        class="form-control !mt-10"
        label="Your date of birth"
        label-type="floating"
        placeholder="DD/MM/YYYY"
      />
    </BxField>
    <BxField v-if="applicantDetailsType === 'comprehensive'" v-slot="{ field }" name="address">
      <BxAddressInput
        :id="values.id + '-address'"
        v-bind="field"
        class="form-control"
        label="Residential Address"
        label-type="floating"
      />
    </BxField>
    <div v-if="brokerId && applicantDetailsType === 'comprehensive'" class="applicant-note">
      <strong>Contact details</strong>
      <p>
        These details must belong to the applicant. <br />
        You will not be able to submit using your own details.
      </p>
    </div>
    <template v-if="applicantDetailsType === 'comprehensive' || type === ApplicantType.PRIMARY">
      <BxField v-slot="{ field }" name="mobile">
        <BxInput
          :id="values.id + '-mobile-phone-number'"
          v-bind="field"
          class="form-control"
          inputmode="tel"
          label="Mobile number"
          label-type="floating"
          minlength="10"
          :maxlength="values.mobile?.startsWith('+') ? 15 : 10"
          @keypress="onlyValidPhoneKeys"
        />
      </BxField>
      <BxField v-slot="{ field }" name="email">
        <BxInput
          :id="values.id + '-email'"
          v-bind="field"
          class="form-control"
          inputmode="email"
          label="Email address"
          label-type="floating"
          @keypress="noWhiteSpaceKeys"
        />
      </BxField>
      <BxField v-slot="{ field }" name="confirmEmail">
        <BxInput
          :id="values.id + '-confirm-email'"
          v-bind="field"
          class="form-control"
          inputmode="email"
          label="Confirm email address"
          label-type="floating"
          @keypress="noWhiteSpaceKeys"
        />
      </BxField>
    </template>
    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </form>
</template>

<style lang="scss" scoped>
.applicant-note {
  background: #f5f9fc;
  width: fit-content;
  padding: 0.6rem;
  border-radius: 0.3rem;
  margin: 1rem 0;

  strong,
  p {
    font-size: 1rem;
    line-height: 1.3rem;
  }

  strong {
    color: #040a34;
  }

  p {
    font-weight: 500;
    color: #02864e;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }
}
</style>
